/**
 * The honest lens — the light rig's GEOMETRY as data (UX review §3.5).
 *
 * v2's finding, measured: key azimuth = camera azimuth on all four camera
 * paths, difference 0.000000° — a pure butterfly. The terminator ran only
 * horizontally, so axial twist (forearm roll, thigh twist, torso turn — the
 * substance of a pose) was the one thing the lens could not show. The cure is
 * geometric, not aesthetic: the key must sit OFF the viewing azimuth so a
 * VERTICAL terminator exists. This module holds that geometry as plain data
 * with pure math over it, headless-testable — the invariant is the angle,
 * never a screenshot.
 *
 * Wattage (intensities, opacity) lives here too but is taste, tuned by eye in
 * the dev loop; the tests pin the GEOMETRY only.
 * Depends on: nothing (pure).
 * Depended on by: viewport, lens tests.
 */

export interface Vec3Like {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/**
 * Direction from the framed subject's center to the camera — the one framing
 * constant (was inline in frameSubject; now shared so the terminator test and
 * the runtime measure the SAME vector).
 */
export const DEFAULT_VIEW_DIRECTION: Vec3Like = { x: 0.6, y: 0.35, z: 1 };

/** Direction from the orbit target to the boot camera (0,1.6,3) − (0,1,0). */
export const INITIAL_VIEW_DIRECTION: Vec3Like = { x: 0, y: 0.6, z: 3 };

/**
 * Direction from the subject's center to the key light. Azimuth ≈ −25°, on
 * the OPPOSITE side of the lens axis from the framed view (+31°) — ≈56° of
 * horizontal separation, so the left and right flanks of every limb are lit
 * differently and roll about the vertical axis reads as a moving terminator.
 * Elevation ≈40° keeps the horizontal terminator (up/down form) too.
 */
export const KEY_DIRECTION: Vec3Like = { x: -1.4, y: 2.8, z: 3 };

/**
 * The vertical-terminator invariant, as a band: below MIN the rig collapses
 * back toward v2's butterfly (twist unreadable); above MAX the key goes so
 * far side/back that the camera-facing side falls into fill and form flattens
 * the other way. Classic portraiture puts the key 30–60° off the lens axis.
 */
export const MIN_KEY_VIEW_SEPARATION_DEG = 20;
export const MAX_KEY_VIEW_SEPARATION_DEG = 70;

/** Key elevation band: enough height for up/down form, never a top-down butterfly. */
export const MIN_KEY_ELEVATION_DEG = 25;
export const MAX_KEY_ELEVATION_DEG = 55;

/**
 * Wattage — taste, not law (tuned by eye; the honest part is the geometry
 * above). The IBL replaces v2's constant 0.6 ambient: environment irradiance
 * VARIES with the surface normal, and that variation is the form a constant
 * term destroys (§3.5 qualification 2). It is also the only fill that lights
 * a metalness=1 surface at all — v2 rendered metals black.
 */
export const ENVIRONMENT_INTENSITY = 0.45;
export const KEY_INTENSITY = 2.6;
/** Shadow catcher darkness — visible foot-contact evidence, not a void. */
export const CATCHER_OPACITY = 0.4;

/**
 * The stage pool — the chrome surface the contact shadow reads AGAINST. A
 * catcher alone is a black shadow on a black void: invisible (measured
 * 2026-07-25 — the whole sub-0.08-linear stage exits Neutral's flare
 * suppression at ≤10/255). The pool is deliberately tone-map-EXEMPT chrome,
 * not specimen: its color is a design-token grey, faded radially to the void.
 */
export const POOL_COLOR = '#202020';

/**
 * Khronos PBR Neutral's flare suppression, restricted to dark GREYS: for a
 * grey with linear value x < 0.08 the curve subtracts x − 6.25x², so the
 * displayed value is 6.25x² (exact for greys — the offset uses the min
 * channel, and on a grey every channel is the min). Our whole stage lives in
 * this region; measured 2026-07-25, the #0a0a0a background exits at ~0/255.
 */
export function neutralDarkGreyForward(linearGrey: number): number {
  if (linearGrey < 0 || linearGrey >= 0.08) {
    throw new Error(`neutralDarkGreyForward: ${linearGrey} outside the flare-suppression region [0, 0.08)`);
  }
  return 6.25 * linearGrey * linearGrey;
}

/**
 * The inverse: the linear grey to FEED the renderer so a stage token displays
 * as itself. Materials opt out via toneMapped:false; a plain-Color background
 * cannot (it is cleared into the HDR target and rides the output transform),
 * so it gets compensated instead. Valid only below the region's image
 * (6.25·0.08² = 0.04) — stage tokens only, refuses anything brighter.
 */
export function neutralDarkGreyInverse(displayedLinearGrey: number): number {
  if (displayedLinearGrey < 0 || displayedLinearGrey >= 0.04) {
    throw new Error(
      `neutralDarkGreyInverse: ${displayedLinearGrey} is not a dark stage grey — ` +
        `the inverse is only defined below linear 0.04`,
    );
  }
  return Math.sqrt(displayedLinearGrey / 6.25);
}

/** Azimuth about +Y in degrees: atan2(x, z), 0° = looking from +Z. */
export function azimuthDeg(v: Vec3Like): number {
  return (Math.atan2(v.x, v.z) * 180) / Math.PI;
}

/** Elevation above the ground plane in degrees. */
export function elevationDeg(v: Vec3Like): number {
  return (Math.atan2(v.y, Math.hypot(v.x, v.z)) * 180) / Math.PI;
}

/** Absolute azimuthal separation, wrapped to [0, 180]. */
export function azimuthSeparationDeg(a: Vec3Like, b: Vec3Like): number {
  const raw = Math.abs(azimuthDeg(a) - azimuthDeg(b)) % 360;
  return raw > 180 ? 360 - raw : raw;
}

export interface CatcherPlacement {
  /** Plane height — strictly below the subject's measured lowest point. */
  readonly y: number;
  /** Half the plane's edge length. */
  readonly halfSize: number;
}

/**
 * Where the shadow catcher goes, from the subject's MEASURED bounds (the
 * §3.5 Document-slice rule: framing facts derive from measurement, never an
 * assumed 1.8m human). The drop below minY avoids z-fighting soles and grid;
 * the footprint covers an extended pose (a lunge throws a shadow well past
 * the bounding radius at a 40°-elevation key).
 */
/**
 * Rotate a direction about the vertical axis (right-handed: +Z → +X at +π/2).
 * The key light is authored for a +Z face (KEY_DIRECTION); a headshot camera
 * that moves onto the rig's facing axis (ADR-035) takes the key with it by
 * the same turn, or a −Z face is lit from behind (Codex P1, #162).
 */
export function rotateAboutY(v: Vec3Like, rad: number): Vec3Like {
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  return { x: v.x * c + v.z * s, y: v.y, z: -v.x * s + v.z * c };
}

/** A headshot lens (ADR-035) in the shape the camera placement needs — structurally `HeadshotLens`. */
export interface LensLike {
  readonly target: Vec3Like;
  readonly radius: number;
  readonly fovRad: number;
  readonly betaRad: number;
  readonly alphaRad: number;
}

/** Where a camera and the key light stand for a lens: ONE placement, shared by the headshot capture and the live hold. */
export interface LensPlacement {
  readonly camera: Vec3Like;
  readonly target: Vec3Like;
  readonly fovDeg: number;
  readonly near: number;
  readonly far: number;
  readonly key: Vec3Like;
  readonly keyTarget: Vec3Like;
}

/**
 * The lens's camera — World's vertical FOV, radius and polar angle on the facing azimuth (ArcRotate:
 * target + radius·(sinβ·cosα, cosβ, sinβ·sinα)), near/far re-seated from the radius so a close portrait
 * does not clip its own nose — and the KEY turned with it about the vertical through the target (Codex
 * P1, #162: the rig is authored for a +Z face, so a −Z face shot from −Z under the resting key is
 * backlit). Extracted from `captureSquare` so the guided intake's first frame (`holdLens`) stands
 * exactly where the headshot does (INTAKE_GUIDED_FLOW §3.3).
 */
export function lensPlacement(lens: LensLike, keyPosition: Vec3Like): LensPlacement {
  const sb = Math.sin(lens.betaRad);
  const camera = {
    x: lens.target.x + lens.radius * sb * Math.cos(lens.alphaRad),
    y: lens.target.y + lens.radius * Math.cos(lens.betaRad),
    z: lens.target.z + lens.radius * sb * Math.sin(lens.alphaRad),
  };
  const turn = Math.PI / 2 - lens.alphaRad;
  const rel = rotateAboutY({ x: keyPosition.x - lens.target.x, y: keyPosition.y - lens.target.y, z: keyPosition.z - lens.target.z }, turn);
  return {
    camera,
    target: { x: lens.target.x, y: lens.target.y, z: lens.target.z },
    fovDeg: (lens.fovRad * 180) / Math.PI,
    near: Math.max(lens.radius / 100, 0.001),
    far: Math.max(lens.radius * 100, 50),
    key: { x: lens.target.x + rel.x, y: lens.target.y + rel.y, z: lens.target.z + rel.z },
    keyTarget: { x: lens.target.x, y: lens.target.y, z: lens.target.z },
  };
}

export function catcherPlacement(bounds: { readonly minY: number; readonly radius: number }): CatcherPlacement {
  return {
    y: bounds.minY - 0.002,
    halfSize: Math.max(bounds.radius * 2.5, 0.5),
  };
}
